<script setup lang="ts">
import SearchAttributeContents from "@/components/CvrConvert/VehiclesListParts/SearchAttributeContents.vue"
import HeaderElement from '@/components/CvrConvert/VehiclesListParts/HeaderElement.vue'
import OperationOfficeFilter from "@/components/CvrConvert/OperationOfficeFilter.vue";
import getCvrUploadSummariesResponse from "@/types/responses/getCvrUploadSummaries"
import VehiclesRow from "@/components/CvrConvert/VehiclesListParts/VehiclesRow.vue"
import getCvrUploadSummariesRequest from "@/types/requests/getCvrUploadSummaries"
import cvrUploadSummariesData from "@/types/responses/cvrUploadSummariesData"
import getOperationOfficeType from '@/types/responses/getOperationOffice';
import { Ref, ref, reactive, watch, computed, onMounted } from 'vue'
import getDepartmentResponse from '@/types/responses/getDepartment'
import PaginateWithCount from "@/components/PaginateWithCount.vue"
import getGroupResponse from '@/types/responses/getGroup'
import Api from "@/services/api/ApiServiceFabrick"
import i18n from '@/lang/i18n'
import {
  CVR_UPLOAD_READY_STATUSES,
  CVR_UPLOAD_COMPLETE_STATUSES,
  MANAGEMENT_OFFICE_TYPE,
  SUB_MANAGEMENT_OFFICE_TYPE,
  DEPARTMENT_OFFICE_TYPE
} from "@/services/consts"

const props = defineProps<{
  reload: () => void
  showFlash: (message: string, type: string, fadeout: boolean) => { value: any }
  openCvrList: (vehicle: cvrUploadSummariesData) => void
  isDisplayCvrList: boolean
  selectedVehicle: cvrUploadSummariesData
  createAuthority: boolean
}>()

type getCvrUploadSummariesParamsType = {
  page: number
} | null | undefined

type selectableItemsType = {
  [key: string]: cvrUploadSummariesData[] | getDepartmentResponse[] | getGroupResponse[];
  vehicle: cvrUploadSummariesData[];
  department: getDepartmentResponse[];
  groups: getGroupResponse[];
};

type searchParamsType = {
  [key: string]: any;
  vehicle: {key: number, value: boolean}[];
  department: {key: number, value: boolean}[];
  groups: {key: number, value: boolean}[];
  upload_statuses: {key: string, value: string}[];
};

type formType = {
  [key: string]: any;
  vehicle: {key: number, value: boolean}[];
  department: {key: number, value: boolean}[];
  groups: {key: number, value: boolean}[];
  upload_statuses: {key: string, value: string}[];
  page: number;
}

const VEHICLE_KEY = 'vehicle'
const DEPARTMENT_KEY = 'department'
const GROUP_KEY = 'groups'
const UPLOAD_STATUS_KEY = 'upload_statuses'
const DEFAULT_UPLOAD_STATUSES = [
  {key: 'ready', value: i18n.global.t('pages.cvr_uploads.waitting')},
  {key: 'completed', value: i18n.global.t('pages.cvr_uploads.requestAvailable')}
]
const NOT_AFFILIATED_COLUMN = {
  id: 0,
  name: i18n.global.t('pages.cvr_uploads.list.notAffiliated')
}
const searchWindowStatus: Ref<boolean> = ref(false);
const selectedElement = reactive({
  bottom: 0,
  left: 0,
  width: 0,
});
const sortKey: Ref<string> = ref(VEHICLE_KEY);
const selectableItems: selectableItemsType = reactive({
  vehicle: [] as cvrUploadSummariesData[],
  department: [] as getDepartmentResponse[],
  groups: [] as getGroupResponse[]
})
const allDepartments: Ref<getDepartmentResponse[]> = ref([])
const allGroups: Ref<getGroupResponse[]> = ref([])
const searchParams: searchParamsType = reactive({
  vehicle: [] as {key: number, value: boolean}[],
  department: [] as {key: number, value: boolean}[],
  groups: [] as {key: number, value: boolean}[],
  upload_statuses: DEFAULT_UPLOAD_STATUSES as {key: string, value: string}[],
})
const form: formType = reactive({
  vehicle: [] as {key: number, value: boolean}[],
  department: [] as {key: number, value: boolean}[],
  groups: [] as {key: number, value: boolean}[],
  upload_statuses: DEFAULT_UPLOAD_STATUSES as {key: string, value: string}[],
  page: 1 as number
})
const sortType: Ref<string> = ref('ASC')
const openWindowKey: Ref<string> = ref('')
const searchedKeys: Ref<string[]> = ref([])
const cvrUploadSummaries: Ref<getCvrUploadSummariesResponse> = ref({
  data: [],
  total_page: 1,
  total: 0,
  per_page: 50,
  current_page: 1
})
const itemsPerPage: Ref<number> = ref(50)
const allElementsCount: Ref<number> = ref(0)
const cvrUploadSummariesList: Ref<cvrUploadSummariesData[]> = ref([])
const isLoading: Ref<boolean> = ref(false)
const isSummariesListLoading: Ref<boolean> = ref(false)
const isOpenSearchWindow: Ref<boolean> = ref(false)
const isDepartment: Ref<boolean> = ref(false)
const isDisplayOperationOfficeFilter = ref(false);
const isSearched: Ref<boolean> = ref(false);
const selectedOperationOfficeId: Ref<number | null> = ref(null)
const selectedOperationOffice: Ref<getOperationOfficeType | null> = ref(null);
const isAllSelectedByWindowKey: Ref<{[key: string]: boolean}> = ref({})

onMounted( async() => {
  await getSelfAccount()
  if (isDisplayOperationOfficeFilter.value) {
    getAutoComplets()
  } else {
    requestApis()
  }
})

const searchTextBoxLabel = computed(() => {
  switch (openWindowKey.value) {
    case VEHICLE_KEY:
      return i18n.global.t('pages.cvr_uploads.list.numberPlate')
    case DEPARTMENT_KEY:
      return i18n.global.t('pages.cvr_uploads.list.department')
    case GROUP_KEY:
      return i18n.global.t('pages.cvr_uploads.list.group')
    default:
      return ''
  }
})

async function getSelfAccount() {
  await Api().getSelfAccount().then((data) => {
    if (
      data.login_organization_type == MANAGEMENT_OFFICE_TYPE ||
      data.login_organization_type == SUB_MANAGEMENT_OFFICE_TYPE
    ) {
      isDisplayOperationOfficeFilter.value = true
    }
  }).catch((error) => {
    const res = error.response
    if (res.status == 403) {
      location.reload();
    }
  })
}

async function requestApis(params: getCvrUploadSummariesParamsType = null) {
  await Promise.all([
    getSummaries(params),
    getAutoComplets()
  ])
  makeSelectableItems();
}

async function getSummaries(params: getCvrUploadSummariesParamsType = null) {
  isSearched.value = true
  await Promise.all([
    getAllCvrUploadSummaries(),
    getCvrUploadSummaries(params)
  ])
}

async function getAutoComplets() {
  await Promise.all([
    getDepartment(),
    getGroups(),
    getSelfOffice()
  ])
}

async function getAllCvrUploadSummaries() {
  isSummariesListLoading.value = true
  let requestParams = {} as getCvrUploadSummariesRequest
  if (selectedOperationOfficeId.value) {
    requestParams.operation_office_id = selectedOperationOfficeId.value
  }

  await Api().getCvrUploadSummaries(requestParams).then((data: cvrUploadSummariesData[]) => {
    cvrUploadSummariesList.value = data
    selectableItems.vehicle = data
    setDefaultStatus(VEHICLE_KEY)
    isSummariesListLoading.value = false
  })
}

async function getDepartment() {
  await Api().autoCompleteDepartments().then((data: getDepartmentResponse[]) => {
    allDepartments.value = data
  })
}

async function getGroups() {
  await Api().autoCompleteGroups(makeGetGroupsQueryParams()).then((data: getGroupResponse[]) => {
    allGroups.value = data
  })
}

function makeGetGroupsQueryParams() {
  return {
    status: 1
  };
}

async function getSelfOffice() {
  Api().getSelfAccount().then((data) => {
    isDepartment.value = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
  })
}

function setDefaultStatus(key: string) {
  selectableItems[key].forEach((value: any) => {
    searchParams[key][value.id] = true
  })
  form[key] = JSON.parse(JSON.stringify(searchParams[key]))
}

async function getCvrUploadSummaries(params: getCvrUploadSummariesParamsType = null) {
  isLoading.value = true
  const requestParams = makeRequestParams(params)
  await Api().getCvrUploadSummaries(requestParams).then((data: getCvrUploadSummariesResponse) => {
    if (isSearched.value) {
      cvrUploadSummaries.value = data
      form.page = data.current_page
      allElementsCount.value = data.total
      itemsPerPage.value = data.per_page
    }
    isLoading.value = false
  })
}

function makeSelectableItems() {
  selectableItems.department = filterBySummaryVehicles(allDepartments.value, DEPARTMENT_KEY)
  setDefaultStatus(DEPARTMENT_KEY)
  selectableItems.groups = filterBySummaryVehicles(allGroups.value, GROUP_KEY)
  setDefaultStatus(GROUP_KEY)
}

function filterBySummaryVehicles(values: getDepartmentResponse[] | getGroupResponse[], attribute: string) {
  let items: getDepartmentResponse[] | getGroupResponse[] = []
  values.forEach((value: getDepartmentResponse | getGroupResponse) => {
    selectableItems.vehicle.forEach((vehicle: cvrUploadSummariesData) => {
      if (attribute == DEPARTMENT_KEY) {
        if (!isDepartment.value && vehicle.department_id && isIncluedsNotAffiliatedColumn(items)) {
          items.push(NOT_AFFILIATED_COLUMN);
        }
        if (isAddSelectableDepartment(value, vehicle, items)) {
          return items.push(value);
        }
      }
      if (attribute == GROUP_KEY) {
        if (!vehicle.group_ids.length && isIncluedsNotAffiliatedColumn(items)) {
          items.push(NOT_AFFILIATED_COLUMN);
        }
        if (isAddSelectableGroups(value, vehicle, items)) {
          return items.push(value);
        }
      }
    })
  })
  return items
}

function isIncluedsNotAffiliatedColumn(items: any[]) {
  return !items.find((item) => {
    return item.id === 0
  })
}

function isAddSelectableDepartment(value: getDepartmentResponse, vehicle: cvrUploadSummariesData, items: getDepartmentResponse[]) {
  return value.id == vehicle.department_id && !items.find((item) => { return item.id == value.id })
}

function isAddSelectableGroups(value: getGroupResponse, vehicle: cvrUploadSummariesData, items: getGroupResponse[]) {
  return vehicle.group_ids.includes(value.id) && !items.find((item) => { return item.id == value.id })
}

function makeRequestParams(params: getCvrUploadSummariesParamsType = null): getCvrUploadSummariesRequest {
  return {
    operation_office_id: selectedOperationOfficeId.value,
    device_ids: makeRequestIds(form.vehicle, VEHICLE_KEY, 'device_id'),
    department_ids: makeRequestIds(form.department, DEPARTMENT_KEY, 'id'),
    group_ids: makeRequestIds(form.groups, GROUP_KEY, 'id'),
    statuses: makeStatuses(form.upload_statuses),
    sort_key: makeRequestSortKey(),
    sort_type: sortType.value,
    page: params?.page ?? form.page
  }
}

function makeRequestIds(values: any, key: string, addAttribute: string) {
  let ids: (number | null)[] = []
  let falseCount = 0
  values.forEach((value: boolean | null, id: number) => {
    if (value === false) {
      return falseCount++
    }
    if (value) {
      if (id === 0) {
        return ids.push(null)
      }
      selectableItems[key].find((item: any) => {
        if (item.id == id) {
          ids.push(item[addAttribute])
        }
      })
    }
  })
  if (falseCount === 0) {
    return []
  }
  return ids
}

function makeStatuses(statuses: any) {
  if (!statuses.length || statuses.length == 2) {
    return [];
  }
  let responseStatuses: string[] = []
  statuses.forEach((status: {key: string, value: string}) => {
    if (status.key == 'ready') {
      responseStatuses.push(...CVR_UPLOAD_READY_STATUSES)
    } else if (status.key == 'completed') {
      responseStatuses.push(...CVR_UPLOAD_COMPLETE_STATUSES)
    }
  })
  return responseStatuses
}

function makeRequestSortKey() {
  switch(sortKey.value) {
    case DEPARTMENT_KEY:
      return 'department_name'
    case GROUP_KEY:
      return 'group_names'
    default:
      return 'number_plate'
  }
}

const openSearchWindow = (key: string) => {
  if (props.isDisplayCvrList) return
  isOpenSearchWindow.value = true
  if (sortKey.value != key) sortType.value = 'ASC'
  openWindowKey.value = key

  const selectedContent: HTMLElement | null = document.getElementById(key)
  const element = selectedContent?.getBoundingClientRect()
  if (!element) return
  selectedElement.bottom = element.bottom + window.scrollY + 5
  selectedElement.left = element.left + window.scrollX
  selectedElement.width = element.width

  changeWindowStatus(true)
}

const changeSortType = (type: string) => {
  sortType.value = type
  sortKey.value = openWindowKey.value
  getCvrUploadSummaries()
  changeWindowStatus(false)
}

const changeWindowStatus = (status: boolean) => {
  if (!status) {
    searchParams[openWindowKey.value] = JSON.parse(JSON.stringify(form[openWindowKey.value]));
  }
  searchWindowStatus.value = status
}

const search = (key: string) => {
  form.page = 1
  form[key] = JSON.parse(JSON.stringify(searchParams[key]))
  getCvrUploadSummaries()
  changeWindowStatus(false)
}

async function searchSummaries(params: getCvrUploadSummariesParamsType) {
  await getSummaries(params)
  makeSelectableItems();
}

function setIsAllSelectedByWindowKey($event: { windowKey: string, isAllSelected: boolean }) {
  isAllSelectedByWindowKey.value[$event.windowKey] = $event.isAllSelected
}

const vehicleWidth = computed(() => {
  return props.isDisplayCvrList ? 'vehicle-header closed' : 'vehicle-heade';
})

const acordionItemStyle = computed(() => {
  return props.isDisplayCvrList ? 'display: none' : '';
})

const acordionHeaderClassName = computed(() => {
  return props.isDisplayCvrList ? 'acordion-headers closed' : 'acordion-headers';
})

const listClassName = computed(() => {
  return props.isDisplayCvrList ? 'cvr-list-table close' : 'cvr-list-table'
})

watch(() => form.page, () => {
  getCvrUploadSummaries()
})

watch(() => selectedOperationOffice.value, (operationOffice) => {
  selectedOperationOfficeId.value = operationOffice?.id ?? null
  if (operationOffice) {
    const params = {
      page: 1
    }
    return searchSummaries(params)
  }
  isSearched.value = false
  cvrUploadSummaries.value.data = []
})

defineExpose({ getCvrUploadSummaries })
</script>
<template>
  <OperationOfficeFilter
    v-show="!isDisplayCvrList && isDisplayOperationOfficeFilter"
    v-model="selectedOperationOffice"
  />
  <div
    class="list mt-3 position-relative"
    :class="listClassName"
  >
    <template v-if="isDisplayOperationOfficeFilter && !isSearched">
      <div class="not-searched">
        <label>{{ i18n.global.t('pages.cvr_uploads.list.pleaseSelectOperationOffice') }}</label>
      </div>
    </template>
    <template v-else-if="isLoading || isSummariesListLoading">
      <div class="not-searched">
        <label>{{ i18n.global.t('pages.cvr_uploads.requestPanel.loading') }}</label>
      </div>
    </template>
    <div class="list table-scrollbar mb-2">
      <v-table
        :class="acordionHeaderClassName"
      >
        <thead
          class="position-relative"
          style="z-index: 100"
        >
          <tr>
            <th
              :id="VEHICLE_KEY"
              class="col-md-2 searchable"
              :class="vehicleWidth"
              @click="openSearchWindow(VEHICLE_KEY)"
            >
              <HeaderElement
                :sortKey="sortKey"
                :sortType="sortType"
                :elementKey="VEHICLE_KEY"
                :isAllSelected="isAllSelectedByWindowKey[VEHICLE_KEY]"
                :searchable="true"
                :sortable="true"
                :title="i18n.global.t('pages.cvr_uploads.list.numberPlate')"
                :titleWidth="props.isDisplayCvrList ? '100%' : '60%'"
              />
            </th>
            <th
              :id="DEPARTMENT_KEY"
              class="col-md-2 searchable"
              :style="acordionItemStyle"
              @click="openSearchWindow(DEPARTMENT_KEY)"
            >
              <HeaderElement
                :sortKey="sortKey"
                :sortType="sortType"
                :elementKey="DEPARTMENT_KEY"
                :isAllSelected="isAllSelectedByWindowKey[DEPARTMENT_KEY]"
                :searchable="true"
                :sortable="true"
                :title="i18n.global.t('pages.cvr_uploads.list.department')"
                titleWidth="60%"
              />
            </th>
            <th
              :id="GROUP_KEY"
              class="col-md-2 searchable"
              :style="acordionItemStyle"
              @click="openSearchWindow(GROUP_KEY)"
            >
              <HeaderElement
                :sortKey="sortKey"
                :sortType="sortType"
                :elementKey="GROUP_KEY"
                :isAllSelected="isAllSelectedByWindowKey[GROUP_KEY]"
                :searchable="true"
                :sortable="true"
                :title="i18n.global.t('pages.cvr_uploads.list.group')"
                titleWidth="60%"
              />
            </th>
            <th
              class="col-md-1 text-center"
              :style="acordionItemStyle"
            >
              {{ i18n.global.t('pages.cvr_uploads.list.deviceModelType') }}
            </th>
            <th
              class="col-md-1 text-center"
              :style="acordionItemStyle"
            >
              {{ i18n.global.t('pages.cvr_uploads.list.svrCount') }}
            </th>
            <th class="col-md-1"></th>
            <th
              :id="UPLOAD_STATUS_KEY"
              class="col-md-2 searchable"
              :style="acordionItemStyle"
              @click="openSearchWindow(UPLOAD_STATUS_KEY)"
            >
              <HeaderElement
                :sortKey="sortKey"
                :sortType="sortType"
                :elementKey="UPLOAD_STATUS_KEY"
                :isAllSelected="isAllSelectedByWindowKey[UPLOAD_STATUS_KEY]"
                :searchable="true"
                :sortable="true"
                :title="i18n.global.t('pages.cvr_uploads.list.uploadStatus')"
                titleWidth="70%"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <template
            v-if="!isLoading && !isSummariesListLoading && !cvrUploadSummaries.data.length && isSearched"
          >
            <label>{{ i18n.global.t('pages.cvr_uploads.list.noVehicle') }}</label>
          </template>
          <template
            v-else
            v-for="(item, index) in cvrUploadSummaries.data"
            :key="index"
          >
            <VehiclesRow
              :item="item"
              :reload="props.reload"
              :showFlash="props.showFlash"
              :vehicleWidth="vehicleWidth"
              :acordionItemStyle="acordionItemStyle"
              :isDisplayCvrList="props.isDisplayCvrList"
              :vehicle="props.selectedVehicle"
              :searchWindowStatus="searchWindowStatus"
              :createAuthority="props.createAuthority"
              @openCvrList="props.openCvrList"
            />
          </template>
        </tbody>
      </v-table>
    </div>
    <PaginateWithCount
      v-if="!props.isDisplayCvrList && isSearched"
      class="justify-end"
      :totalPage="cvrUploadSummaries.total_page"
      v-model="form.page"
      :itemsPerPage="itemsPerPage"
      :allElementsCount="allElementsCount"
      :showItemCount="true"
    />
  </div>
  <SearchAttributeContents
    v-if="searchWindowStatus"
    v-model="searchParams[openWindowKey]"
    :filteredItems="form"
    :items="selectableItems"
    :windowKey="openWindowKey"
    :sortKey="sortKey"
    :sortType="sortType"
    :label="searchTextBoxLabel"
    :searchedKeys="searchedKeys"
    :summariesList="cvrUploadSummariesList"
    :style="`position: absolute; top: ${selectedElement.bottom}px; left: ${selectedElement.left}px;`"
    :width="selectedElement.width"
    @changeSortType="changeSortType"
    @closeWindow="changeWindowStatus"
    @search="search"
    @setIsAllSelected="setIsAllSelectedByWindowKey"
  />
</template>
<style scoped>
.searchable:hover {
  background-color: #dcdcdc;
  transition: 0.3s;
  cursor: pointer;
}
.cvr-list-table {
  width: 100%;
  transition: 0.5s;
}
.cvr-list-table.close {
  width: 10%;
}
.acordion-headers {
  width: 100%;
  transition: 0.5s;
}
.acordion-headers.closed {
  width: 100%;
}
.vehicle-header {
  transition: 0.5s;
}
.vehicle-header.closed {
  width: 100%;
}
.acordion-items.closed {
  display: none;
}
</style>